import { useEffect, useMemo } from 'react';

import { useOrders } from '@/hooks/useOrders';
import { useAuthStore } from '@/stores/authStore';
import { todayISO } from '@/lib/time';
import type { OrderStatus } from '@/types/api';

interface NavigatorBadging {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

const ACTIVE_STATUSES: OrderStatus[] = ['paid', 'pending', 'preparing'];

/**
 * Drives the installed-PWA home-screen badge.
 *
 * Subscribes to today's orders (React Query dedupes with OrdersPage's own
 * subscription, so no extra network), counts active orders, and calls
 * `navigator.setAppBadge(count)`. Falls back silently on browsers without
 * the Badging API (Safari, Firefox).
 *
 * Mount once at the App root so the badge keeps updating while the cashier
 * is on /pickup, /history, or /settings.
 */
export function useAppBadge() {
  const isAuthenticated = useAuthStore((s) => s.status === 'authenticated');
  const today = todayISO();
  const { data } = useOrders(
    { date: today, limit: 200 },
    { enabled: isAuthenticated },
  );

  const activeCount = useMemo(() => {
    if (!isAuthenticated) return 0;
    return (data?.items ?? []).filter((o) => ACTIVE_STATUSES.includes(o.status)).length;
  }, [data, isAuthenticated]);

  useEffect(() => {
    const nav = navigator as Navigator & NavigatorBadging;
    if (!nav.setAppBadge) return;

    if (activeCount > 0) {
      nav.setAppBadge(activeCount).catch(() => {
        /* unsupported or no permission — silently ignore */
      });
    } else {
      nav.clearAppBadge?.().catch(() => {
        /* idem */
      });
    }
  }, [activeCount]);

  // Belt-and-braces: clear badge when the page is hidden for a long time or unloaded.
  useEffect(() => {
    const nav = navigator as Navigator & NavigatorBadging;
    const clear = () => {
      nav.clearAppBadge?.().catch(() => {});
    };
    window.addEventListener('beforeunload', clear);
    return () => {
      window.removeEventListener('beforeunload', clear);
    };
  }, []);
}
